import { type Meta } from '../types/docs';
import { WORKSPACE_AGENT_ELICITATION_COMPONENT_TYPES } from './useTrackAgentElicitationRequested.meta';
import { WORKSPACE_AGENT_ANALYTICS_CATEGORY_META_PARAM } from './workspaceAgentAnalytics';

export const WORKSPACE_AGENT_ELICITATION_RESPONDED_CTAS = ['approve', 'cancel'] as const;

export const WORKSPACE_AGENT_ELICITATION_DECISIONS = ['confirm', 'cancel', 'n/a'] as const;

export type WorkspaceAgentElicitationDecision = (typeof WORKSPACE_AGENT_ELICITATION_DECISIONS)[number];

export type WorkspaceAgentElicitationRespondedCta = (typeof WORKSPACE_AGENT_ELICITATION_RESPONDED_CTAS)[number];

export const WORKSPACE_AGENT_ELICITATION_RESPONDED_TYPES = [
  'tool_consent',
  'binary_choice',
  'single_select',
  'multi_select',
  'describe',
  'text_fields',
  'stepper',
  'search_select',
  'guided_flow',
  'unknown',
] as const;

export type WorkspaceAgentElicitationRespondedType = (typeof WORKSPACE_AGENT_ELICITATION_RESPONDED_TYPES)[number];

const META: Meta = {
  description:
    'The user responds to a workspace agent elicitation dock, or picks a tool in a guided flow. tool_consent maps Approve and Cancel. ' +
    'binary_choice maps the primary label to approve and the decline label (including Escape) to cancel. ' +
    'Choice, text, stepper, and search docks map Continue to approve and Cancel or Escape to cancel. ' +
    'A guided flow maps each tool pick or switch to approve, with the edit id in selected_option_ids.',
  name: 'agent_elicitation_responded',
  params: [
    WORKSPACE_AGENT_ANALYTICS_CATEGORY_META_PARAM,
    {
      description: 'Which consent dock CTA the user activated. A guided flow maps each tool pick or switch to approve.',
      name: 'cta',
      required: true,
      type: 'enum',
      values: [...WORKSPACE_AGENT_ELICITATION_RESPONDED_CTAS],
    },
    {
      description: 'Whether the Allow for this thread checkbox was checked when the user responded',
      name: 'remember_for_session',
      required: true,
      type: 'boolean',
    },
    {
      description:
        'How the frontend classified the pending elicitation row. guided_flow is the guided variants wizard, not a dock.',
      name: 'elicitation_type',
      required: true,
      type: 'enum',
      values: [...WORKSPACE_AGENT_ELICITATION_RESPONDED_TYPES],
    },
    {
      description:
        'The pending elicitation id. Joins this event to agent_elicitation_requested. Empty for a guided flow, which has no elicitation row.',
      name: 'elicitation_id',
      required: true,
      type: 'string',
    },
    {
      description:
        'The agent thread (session id). Empty when a guided flow is answered before a thread exists, which is the zero-state and onboarding case.',
      name: 'chat_id',
      required: true,
      type: 'string',
    },
    {
      description:
        'Spec name for the dock. Ask types use the spec enum; approval docks are tool_consent. Guided flows are guided_flow.',
      name: 'component_type',
      required: true,
      type: 'enum',
      values: [...WORKSPACE_AGENT_ELICITATION_COMPONENT_TYPES],
    },
    {
      description: '1-based position in a chained flow. Standalone docks are 1.',
      name: 'step_index',
      required: true,
      type: 'number',
    },
    {
      description: 'confirm or cancel for a yes/no dock. n/a for every other dock.',
      name: 'decision',
      required: true,
      type: 'enum',
      values: [...WORKSPACE_AGENT_ELICITATION_DECISIONS],
    },
    {
      description: 'True when the submitted answer used Something else',
      name: 'used_escape_hatch',
      required: false,
      type: 'boolean',
    },
    {
      description:
        'Indexes of the chosen options, as strings. Dock options have no separate ids. A guided flow sends the chosen edit id instead, using the same ids as agent_task_submitted edit_id.',
      name: 'selected_option_ids',
      required: false,
      type: 'array',
      values: ['string'],
    },
    {
      description: 'How many options were submitted',
      name: 'selected_count',
      required: false,
      type: 'number',
    },
    {
      description: 'Milliseconds from elicitation.createdAt until the CTA click (client clock)',
      name: 'response_time_ms',
      required: false,
      type: 'number',
    },
    {
      description: 'Same clock as response_time_ms, under the spec property name',
      name: 'time_to_submit_ms',
      required: false,
      type: 'number',
    },
    {
      description: 'The mutating tool name when elicitation_type is tool_consent',
      name: 'tool_name',
      required: false,
      type: 'string',
    },
  ],
};

export default META;
